import useFormattedDate from "@/dates/use-formatted-date";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type { ReturnRequestStatusDto } from "@babackapp/private-api-client";
import { cn } from "@babackapp/customer-ui/lib/utils";

type Props = {
  status: ReturnRequestStatusDto;
};

type Stage = {
  key: keyof ReturnRequestStatusDto;
  translationKey: string;
};

const STAGES: Stage[] = [
  { key: "created", translationKey: "returnRequests.details.timeline.created" },
  { key: "waiting", translationKey: "returnRequests.details.timeline.waiting" },
  { key: "approved", translationKey: "returnRequests.details.timeline.approved" },
  { key: "shipping", translationKey: "returnRequests.details.timeline.shipping" },
  { key: "shipped", translationKey: "returnRequests.details.timeline.shipped" },
  { key: "partiallyTreatedLogistician", translationKey: "returnRequests.details.timeline.partiallyTreatedLogistician" },
  { key: "treatedLogistician", translationKey: "returnRequests.details.timeline.treatedLogistician" },
  { key: "partiallyTreatedMerchant", translationKey: "returnRequests.details.timeline.partiallyTreatedMerchant" },
  { key: "treatedMerchant", translationKey: "returnRequests.details.timeline.treatedMerchant" },
  { key: "resolved", translationKey: "returnRequests.details.timeline.resolved" },
  { key: "cancelled", translationKey: "returnRequests.details.timeline.cancelled" },
  { key: "error", translationKey: "returnRequests.details.timeline.error" },
];

type TimelineEntry = {
  key: keyof ReturnRequestStatusDto;
  translationKey: string;
  doneAt: number;
};

function resolveEntries(status: ReturnRequestStatusDto): TimelineEntry[] {
  return STAGES
    .filter((s) => status[s.key].done)
    .map((s) => ({ ...s, doneAt: status[s.key].doneAt }))
    .sort((a, b) => b.doneAt - a.doneAt);
}

export function ReturnStatusTimeline({ status }: Props) {
  const t = useCustomText();
  const entries = resolveEntries(status);

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-foreground mb-2">
        {t("returnRequests.details.timeline.title")}
      </p>
      <div className="flex flex-col">
        {entries.map((entry, index) => (
          <TimelineItem
            key={entry.key}
            translationKey={entry.translationKey}
            doneAt={entry.doneAt}
            isLatest={index === 0}
            isLast={index === entries.length - 1}
            isCancelled={entry.key === "cancelled" || entry.key === "error"}
          />
        ))}
      </div>
    </div>
  );
}

type TimelineItemProps = {
  translationKey: string;
  doneAt: number;
  isLatest: boolean;
  isLast: boolean;
  isCancelled: boolean;
};

function TimelineItem({ translationKey, doneAt, isLatest, isLast, isCancelled }: TimelineItemProps) {
  const t = useCustomText();
  const { formattedDate } = useFormattedDate(doneAt);

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "size-2.5 rounded-full mt-1 shrink-0",
            isCancelled ? "bg-destructive" : isLatest ? "bg-primary" : "bg-muted-foreground/40",
          )}
        />
        {!isLast && <div className="w-px flex-1 bg-border mt-1 mb-1" />}
      </div>
      <div className={cn("flex flex-col pb-4", isLast && "pb-0")}>
        <span className={cn(
          "text-sm font-medium",
          isCancelled ? "text-destructive" : isLatest ? "text-foreground" : "text-muted-foreground",
        )}>
          {t(translationKey)}
        </span>
        <span className="text-xs text-muted-foreground">{formattedDate}</span>
      </div>
    </div>
  );
}
